// HER ORDER (2026-08-07, after live member complaints) — **THE ORIENTATION LAW.**
//
// *"Only files in the vertical folder inside the rooms folder are mobile art. Everything else in the
// rooms folder is desktop widescreen. The pickers are viewport-scoped: on MOBILE a member sees and
// can choose ONLY vertical art. On DESKTOP only widescreen. Nobody ever sees the other orientation,
// and nothing is ever stretched across the wrong screen."*
//
// **WHAT WENT WRONG, MEASURED BEFORE A LINE WAS WRITTEN.** Whether a row was mobile art was decided
// by whether its SLUG ended in `-v`. Seven rows built from files in the VERTICAL folder
// (`1-V.png`..`7-V.png`, 900x1600) were written under non-`-v` slugs (`mens-p1`..`mens-p7`) — so the
// picker offered them on desktop and the browser stretched portrait art across a widescreen. **The
// slug could never have caught that, because the slug was the thing that was wrong.** Orientation is
// a fact about the ROW now (migration 088), and this module is the one place that says what each
// viewport may be shown.
//
// It imports NOTHING, so `scripts/room-art-orientation.mjs` loads and RUNS it, and the SERVER reads
// the same law it does — a picker filtered on the client would be the same trust-the-wiring shape
// one layer up.

/** The two orientations a row may have, and the third state a row may be IN (R18). */
export const ORIENTATIONS = ["wide", "vertical"];
export const orientationValid = (o) => ORIENTATIONS.includes(String(o || ""));

/**
 * **THE BREAKPOINT, DECLARED ONCE.** The `<picture>` element's `media` attribute and the picker's
 * viewport question have to be the SAME number or a member on a 800px screen is offered widescreen
 * art and served vertical (R28/R29). It was typed into App.tsx and nowhere else.
 */
export const ROOM_ART_BREAKPOINT = 820;
export const ROOM_ART_MOBILE_MEDIA = `(max-width: ${ROOM_ART_BREAKPOINT}px)`;
export const VIEWPORTS = ["desktop", "mobile"];
export const viewportValid = (v) => VIEWPORTS.includes(String(v || ""));
/** Which viewport a width is. Passed IN rather than read off `window`, so a gate can drive it. */
export function viewportFor(width) {
  return Number(width) <= ROOM_ART_BREAKPOINT ? "mobile" : "desktop";
}

/** The slug of a row's vertical twin. The twin is never itself a choice. */
export const verticalSlug = (slug) => `${String(slug || "")}-v`;
export const isVerticalSlug = (slug) => /-v$/.test(String(slug || ""));

/**
 * **WHAT ART A CHOICE ACTUALLY HAS**, which is the question every clause below is really asking.
 *
 * A picker entry is a non-`-v` slug. Two independent facts hang off it and they are NOT the same
 * fact (R18): whether there is WIDE art to put on a desktop, and whether there is VERTICAL art to
 * put on a phone. The seven rows this round is about had vertical art and no wide art while looking,
 * by their slug, exactly like an entry that had both.
 *
 * `row` is `{ slug, orientation }`; `twins` is the set of `-v` slugs that exist.
 */
export function artFor(row, twins) {
  const slug = String(row?.slug || "");
  const own = String(row?.orientation || "");
  const has = twins instanceof Set ? twins : new Set(twins || []);
  return {
    slug,
    // The row's OWN art is wide only when the row says so. An unknown orientation is not wide.
    wide: own === "wide",
    // Vertical art exists either because the row itself is vertical, or because a twin was ingested.
    vertical: own === "vertical" || has.has(verticalSlug(slug)),
  };
}

/**
 * **MAY THIS ENTRY BE OFFERED ON THIS VIEWPORT.** Her law, as one predicate, and the ONLY thing the
 * picker is allowed to ask.
 *
 *   • DESKTOP is offered an entry only when it has WIDE art. `mens-p1` has none, so it can never
 *     appear there again — which is her reported defect made structurally impossible.
 *   • MOBILE is offered an entry only when it has VERTICAL art. That is the half nobody had written
 *     down: six masculine rows are widescreen with no twin at all, and the phone was being handed
 *     the widescreen through the serve route's own fallback.
 *
 * **AN ENTRY WITH NEITHER IS OFFERED NOWHERE**, rather than defaulting into one of them — a row with
 * no dimensions recorded is a row nobody has measured, and guessing puts unmeasured art on a
 * member's screen (R17/R25: an unrostered value draws nothing, never a plausible something).
 */
export function offeredOn(art, viewport) {
  if (!art || isVerticalSlug(art.slug)) return false;
  if (viewport === "desktop") return art.wide === true;
  if (viewport === "mobile") return art.vertical === true;
  return false;
}

/**
 * **WHAT THE ROOM PAINTS, AND THE CLAUSE THAT MAKES "NOTHING IS EVER STRETCHED" TRUE RATHER THAN
 * HOPED FOR.**
 *
 * The picker being viewport-scoped stops a member CHOOSING the wrong orientation. It does not stop
 * her opening on her other device: viewport-only art is exactly what her standing ruling says the
 * thirteen masculine files are, so a phone choice really can be a slug with no widescreen behind it.
 * Rather than stretch it, the room falls back to its default backdrop on that screen.
 *
 * Three answers, not two: `wide` and `vertical` are the art to paint, and `none` means this member's
 * chosen art has nothing for this screen and the plain backdrop is correct.
 */
export function paintFor(art, viewport) {
  if (!art || !art.slug) return "none";
  if (viewport === "mobile") return art.vertical ? "vertical" : "none";
  return art.wide ? "wide" : "none";
}

/**
 * **HER CLAUSE 4, AS A PURE DECISION.** *"Any member whose saved background choice is a
 * wrong-orientation mapping gets their choice remapped to the correct twin where a name-match
 * exists, or reset to the default where none exists, with nothing else touched."*
 *
 * A choice is WRONG when the art behind it cannot serve the screen it was chosen from — which after
 * this round means: an entry with no wide art that a member is holding, since every picker before
 * today was a desktop picker (there has never been a mobile-only picker to have chosen from). The
 * remap looks for a name-matched entry in the SAME SET that does have wide art; the masculine
 * verticals have never paired with the masculine desktops, which is her own standing finding, so
 * those members reset.
 *
 * Returns `{ action, to }`: `keep`, `remap` (with the slug), or `reset` (to the default, `null`).
 * **`keep` is the answer for everything this round is not about**, which is what "nothing else
 * touched" means as arithmetic rather than as a promise.
 */
export function remapChoice(slug, { artBySlug, pairName }) {
  const s = String(slug || "");
  if (!s) return { action: "keep", to: s };
  const art = artBySlug instanceof Map ? artBySlug.get(s) : (artBySlug || {})[s];
  // A slug nobody has a row for is already broken and is NOT this round's business: the room's own
  // serve route answers nothing for it and the member sees the default. Touching it would be a
  // second, unrequested change (R17/R160).
  if (!art) return { action: "keep", to: s };
  if (art.wide) return { action: "keep", to: s };
  // No wide art. Is there a name-matched entry that has some?
  const wants = typeof pairName === "function" ? pairName(s) : null;
  if (wants && wants !== s) {
    const twin = artBySlug instanceof Map ? artBySlug.get(wants) : (artBySlug || {})[wants];
    if (twin && twin.wide) return { action: "remap", to: wants };
  }
  return { action: "reset", to: null };
}
